import React, { useEffect } from 'react';
import {
  Container,
  Heading,
  Section,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { useGetParentAdmins } from 'src/components/enterprise/Parents/Admin/hooks/useGetParentAdmins/useGetParentAdmins';
import { ParentAdminsTable } from 'src/components/enterprise/Parents/Admin/ParentAdminsController/ParentAdminsTable/ParentAdminsTable';
import { ParentAdminRowWrapper } from 'src/components/enterprise/Parents/Admin/ParentAdminsController/ParentAdminsTable/ParentAdminRowWrapper/ParentAdminRowWrapper';
import { ParentAdminDisplay } from 'src/components/enterprise/Parents/Admin/ParentAdminsController/ParentAdminsTable/ParentAdminDisplay/ParentAdminDisplay';
import { ParentAdminTableFormProps } from 'src/components/enterprise/Parents/Admin/ParentAdminsController/types';
import PageSection from 'src/components/enterprise/layout/PageSection';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { useGetParentAccountsRedirectUrl } from 'src/components/enterprise/Parents/Admin/hooks/useGetParentAccountsRedirectUrl/useGetParentAccountsRedirectUrl';
import { ISearchParams } from 'src/utilities/useSearchHooks';
import { TIERED_ACCOUNTS_PAGE_SIZE } from 'src/components/enterprise/Parents/constants';
import { ParentAdminsHeader } from './ParentAdminsHeader/ParentAdminsHeader';

export const ParentAdminsController: React.FC = () => {
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const formProps = useForm<ParentAdminTableFormProps>({
    defaultValues: {
      selectedParentAdminIds: [],
      selectableParentAdminIds: [],
      selectAll: false,
      searchInput: '',
    },
  });

  const { searchResults: parentAdminSearchResults } = useGetParentAdmins(
    corpId,
    TIERED_ACCOUNTS_PAGE_SIZE
  );

  const handleSearchWithParams = (query: string, page = 1) => {
    const { doSearch } = parentAdminSearchResults;
    const params: ISearchParams = { page };
    if (query) {
      // If sending empty string to the query the result list is empty
      params.query = query;
    }

    doSearch(params);
  };
  const handleSearchClick = () => {
    const query = formProps.getValues('searchInput');
    handleSearchWithParams(query);
  };

  const handlePageChange = (page: number) => {
    const query = formProps.getValues('searchInput');
    handleSearchWithParams(query, page);
  };

  const parentAccountsUrl = useGetParentAccountsRedirectUrl();

  const { setValue, register } = formProps;

  useEffect(() => {
    register('selectableParentAdminIds');
  }, [register]);

  useEffect(() => {
    if (parentAdminSearchResults.resultsList) {
      const selectableParentAdminIds = parentAdminSearchResults.resultsList.map(
        ({ diner_id }) => diner_id
      );
      setValue('selectableParentAdminIds', selectableParentAdminIds);
    }
  }, [parentAdminSearchResults.resultsList, setValue]);

  return (
    <Container variant="default">
      <Section inset="sm">
        <PageHeader>
          <Stack y="x-sm">
            <Heading variant="h2">Parent admins</Heading>
          </Stack>
          <Breadcrumbs breadcrumbs={[parentAccountsUrl]} />
        </PageHeader>
        <Stack y="md">
          <PageSection>
            <ParentAdminsHeader corpId={corpId} />
          </PageSection>
        </Stack>

        <PageSection>
          <FormProvider {...formProps}>
            <ParentAdminsTable
              searchResults={parentAdminSearchResults}
              onSearchClick={handleSearchClick}
              onPageChange={handlePageChange}
            >
              <Stack as="ul" className="removeListStyles">
                {parentAdminSearchResults.resultsList.map((parentAdmin) => (
                  <ParentAdminRowWrapper key={parentAdmin.id}>
                    <ParentAdminDisplay
                      firstName={parentAdmin.first_name}
                      lastName={parentAdmin.last_name}
                      dinerId={parentAdmin.diner_id}
                      email={parentAdmin.email}
                      corpId={corpId}
                    />
                  </ParentAdminRowWrapper>
                ))}
              </Stack>
            </ParentAdminsTable>
          </FormProvider>
        </PageSection>
      </Section>
    </Container>
  );
};
